import {
  getMembershipListing,
  createMembershipListing,
  updateMembershipListing,
  deleteMembershipListing,
} from "../../../apis/membershipApi";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { MembershipListing } from "../../../schemas/memberships";
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Typography,
  IconButton,
  CardActions,
  Chip,
  Skeleton,
  Alert,
  Tooltip,
  Grid,
  Container,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  Snackbar,
  FormHelperText,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Image as ImageIcon,
  Close as CloseIcon,
  CloudUpload as UploadIcon,
} from "@mui/icons-material";
import { useState, useRef } from "react";

interface MembershipFormData {
  name: string;
  amount: string;
  type: string;
  imageUrl?: string;
  imageFile?: File;
}

const membershipTypes = [
  { value: "monthly", label: "Monthly" },
  { value: "yearly", label: "Yearly" },
  { value: "biweekly", label: "Biweekly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "semi_annual", label: "Semi-Annual" },
];

const AddMemberships = () => {
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<MembershipListing | null>(
    null
  );
  const [formData, setFormData] = useState<MembershipFormData>({
    name: "",
    amount: "",
    imageUrl: "",
    type: "",
  });
  const [formErrors, setFormErrors] = useState<Partial<MembershipFormData>>({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });
  const [imagePreview, setImagePreview] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const queryClient = useQueryClient();

  // Queries and Mutations
  const membershipPlansQuery = useQuery({
    queryKey: ["membershipPlans"],
    queryFn: getMembershipListing,
  });

  const { data, isLoading, isError } = membershipPlansQuery;

  const createMembershipMutation = useMutation({
    mutationFn: createMembershipListing,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["membershipPlans"] });
      handleCloseForm();
      setSnackbar({
        open: true,
        message: "Membership plan created successfully!",
        severity: "success",
      });
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.message || "Failed to create membership plan",
        severity: "error",
      });
    },
  });

  const updateMembershipMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: FormData }) =>
      updateMembershipListing(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["membershipPlans"] });
      handleCloseForm();
      setSnackbar({
        open: true,
        message: "Membership plan updated successfully!",
        severity: "success",
      });
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.message || "Failed to update membership plan",
        severity: "error",
      });
    },
  });

  const deleteMembershipMutation = useMutation({
    mutationFn: deleteMembershipListing,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["membershipPlans"] });
      setDeleteConfirmId(null);
      setSnackbar({
        open: true,
        message: "Membership plan deleted successfully!",
        severity: "success",
      });
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.message || "Failed to delete membership plan",
        severity: "error",
      });
    },
  });

  // Form validation
  const validateForm = (): boolean => {
    const errors: Partial<MembershipFormData> = {};

    if (!formData.name.trim()) {
      errors.name = "Membership name is required";
    }

    if (!formData.amount.trim()) {
      errors.amount = "Amount is required";
    } else if (isNaN(Number(formData.amount)) || Number(formData.amount) <= 0) {
      errors.amount = "Amount must be a valid positive number";
    }

    if (!formData.type.trim()) {
      errors.type = "Membership type is required";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Event handlers
  const handleEdit = (plan: MembershipListing) => {
    setEditingPlan(plan);
    setFormData({
      name: plan.name,
      amount: plan.amount.toString(),
      imageUrl: plan.imageUrl || "",
      type: plan.type || "",
    });
    setImagePreview(plan.imageUrl || "");
    setIsFormOpen(true);
  };

  const handleDelete = (planId: string) => {
    deleteMembershipMutation.mutate(planId);
  };

  const handleAddNew = () => {
    setEditingPlan(null);
    setFormData({ name: "", amount: "", imageUrl: "", type: "" });
    setImagePreview("");
    setFormErrors({});
    setIsFormOpen(true);
    
    // Force a re-render to debug state issues
    setTimeout(() => {
      console.log("isFormOpen after timeout:", isFormOpen);
    }, 100);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setEditingPlan(null);
    setFormData({ name: "", amount: "", imageUrl: "", type: "" });
    setImagePreview("");
    setFormErrors({});
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);

      setFormData((prev) => ({ ...prev, imageFile: file }));
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    const submitData = new FormData();
    submitData.append("name", formData.name);
    submitData.append("amount", formData.amount);
    submitData.append("type", formData.type.toLowerCase());

    if (formData.imageFile) {
      submitData.append("image", formData.imageFile);
    } else if (formData.imageUrl) {
      submitData.append("imageUrl", formData.imageUrl);
    }

    if (editingPlan) {
      updateMembershipMutation.mutate({ id: editingPlan.id, data: submitData });
    } else {
      createMembershipMutation.mutate(submitData);
    }
  };

  const isSubmitting =
    createMembershipMutation.isPending || updateMembershipMutation.isPending;

  const formatAmount = (amount: number | string) => {
    return typeof amount === "number"
      ? `$${amount.toFixed(2)}`
      : `$${parseFloat(amount.toString()).toFixed(2)}`;
  };

  // Loading skeleton
  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Grid container spacing={3}>
          {[...Array(6)].map((_, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
                <Skeleton variant="rectangular" height={200} />
                <CardContent>
                  <Skeleton variant="text" sx={{ fontSize: "1.5rem", mb: 1 }} />
                  <Skeleton
                    variant="text"
                    sx={{ fontSize: "1rem", width: "60%" }}
                  />
                </CardContent>
                <CardActions>
                  <Skeleton variant="circular" width={40} height={40} />
                  <Skeleton variant="circular" width={40} height={40} />
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    );
  }

  // Error state
  if (isError) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          Failed to load membership plans. Please try again later.
        </Alert>
      </Container>
    );
  }

  // Empty state
  if (!data || data.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="400px"
          textAlign="center"
        >
          <Typography variant="h5" color="text.secondary" gutterBottom>
            No membership plans available
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Get started by creating your first membership plan
          </Typography>
          <Fab
            color="primary"
            variant="extended"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleAddNew();
            }}
            sx={{ borderRadius: 3 }}
          >
            <AddIcon sx={{ mr: 1 }} />
            Add Membership Plan
          </Fab>
        </Box>

        {/* Form Dialog - Moved here to ensure it's always rendered */}
        <Dialog
          open={isFormOpen}
          onClose={handleCloseForm}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 3 },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" fontWeight="bold">
              {editingPlan ? "Edit Membership Plan" : "Add New Membership Plan"}
            </Typography>
            <IconButton onClick={handleCloseForm} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent dividers>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3, py: 1 }}>
              {/* Image Upload Section */}
              <Box>
                <Typography variant="subtitle2" gutterBottom fontWeight="medium">
                  Membership Image
                </Typography>

                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageUpload}
                  accept="image/*"
                  style={{ display: "none" }}
                />

                <Box
                  onClick={() => fileInputRef.current?.click()}
                  sx={{
                    border: "2px dashed",
                    borderColor: "grey.300",
                    borderRadius: 2,
                    p: 3,
                    textAlign: "center",
                    cursor: "pointer",
                    backgroundColor: "grey.50",
                    "&:hover": {
                      borderColor: "primary.main",
                      backgroundColor: "primary.50",
                    },
                  }}
                >
                  {imagePreview ? (
                    <Box>
                      <img
                        src={imagePreview}
                        alt="Preview"
                        style={{
                          width: "100%",
                          maxHeight: "200px",
                          objectFit: "cover",
                          borderRadius: "8px",
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{ mt: 1, color: "text.secondary" }}
                      >
                        Click to change image
                      </Typography>
                    </Box>
                  ) : (
                    <Box>
                      <UploadIcon
                        sx={{ fontSize: 48, color: "grey.400", mb: 1 }}
                      />
                      <Typography variant="body1" color="text.secondary">
                        Click to upload image
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        PNG, JPG, GIF up to 10MB
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Name Field */}
              <TextField
                label="Membership Name"
                value={formData.name}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, name: e.target.value }))
                }
                error={!!formErrors.name}
                helperText={formErrors.name}
                fullWidth
                variant="outlined"
              />

              {/* Amount Field */}
              <TextField
                label="Amount"
                value={formData.amount}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, amount: e.target.value }))
                }
                error={!!formErrors.amount}
                helperText={formErrors.amount}
                fullWidth
                variant="outlined"
                type="number"
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                }}
              />

              <FormControl fullWidth error={!!formErrors.type}>
                <InputLabel id="membership-type-label">
                  Membership Type
                </InputLabel>
                <Select
                  labelId="membership-type-label"
                  label="Membership Type"
                  value={formData.type}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, type: e.target.value }))
                  }
                >
                  {membershipTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.type && (
                  <FormHelperText>{formErrors.type}</FormHelperText>
                )}
              </FormControl>
            </Box>
          </DialogContent>

          <DialogActions sx={{ p: 3 }}>
            <Button onClick={handleCloseForm} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={isSubmitting}
              sx={{ borderRadius: 2, minWidth: "120px" }}
            >
              {isSubmitting ? (
                <CircularProgress size={20} color="inherit" />
              ) : editingPlan ? (
                "Update Plan"
              ) : (
                "Create Plan"
              )}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          <Alert
            onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    );
  }

  return (
    <>
      <Container sx={{ py: 2, width: "100%" }}>
        {/* Header */}
        <Box
          sx={{
            mb: 4,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h4" component="h1" fontWeight="bold">
            Membership Plans
          </Typography>
          <Fab
            color="primary"
            variant="extended"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleAddNew();
            }}
            sx={{ borderRadius: 3 }}
          >
            <AddIcon sx={{ mr: 1 }} />
            Add New Plan
          </Fab>
        </Box>

        {/* Membership Cards Grid */}
        <Grid container spacing={3}>
          {data.map((plan: MembershipListing) => (
            <Grid item xs={12} sm={6} md={4} key={plan.id}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: 3,
                  boxShadow: 2,
                  transition: "all 0.3s ease-in-out",
                  "&:hover": {
                    boxShadow: 6,
                    transform: "translateY(-4px)",
                  },
                }}
              >
                {/* Image Section */}
                {plan.imageUrl ? (
                  <CardMedia
                    component="img"
                    height="220"
                    image={plan.imageUrl}
                    alt={`${plan.name} membership`}
                    sx={{
                      objectFit: "cover",
                      borderTopLeftRadius: 12,
                      borderTopRightRadius: 12,
                    }}
                  />
                ) : (
                  <Box
                    height="220px"
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="center"
                    bgcolor="grey.100"
                    sx={{
                      borderTopLeftRadius: 12,
                      borderTopRightRadius: 12,
                    }}
                  >
                    <ImageIcon
                      sx={{ fontSize: 48, color: "grey.400", mb: 1 }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      No Image Available
                    </Typography>
                  </Box>
                )}

                {/* Content Section */}
                <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                  <Typography
                    variant="h6"
                    component="h2"
                    gutterBottom
                    sx={{
                      fontWeight: 600,
                      lineHeight: 1.3,
                      mb: 2,
                    }}
                  >
                    {plan.name}
                  </Typography>

                  <Chip
                    label={formatAmount(plan.amount)}
                    color="primary"
                    variant="filled"
                    sx={{
                      fontWeight: "bold",
                      fontSize: "0.875rem",
                      borderRadius: 2,
                    }}
                  />
                </CardContent>

                {/* Action Buttons */}
                <CardActions
                  sx={{ pt: 0, pb: 2, px: 2, justifyContent: "flex-end" }}
                >
                  <Tooltip title="Edit membership">
                    <IconButton
                      onClick={() => handleEdit(plan)}
                      size="medium"
                      sx={{
                        color: "primary.main",
                        "&:hover": {
                          backgroundColor: "primary.50",
                        },
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Delete membership">
                    <IconButton
                      onClick={() => setDeleteConfirmId(plan.id)}
                      size="medium"
                      sx={{
                        color: "error.main",
                        "&:hover": {
                          backgroundColor: "error.50",
                        },
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </CardActions>

                {/* Delete Confirmation */}
                {deleteConfirmId === plan.id && (
                  <Box
                    sx={{
                      px: 2,
                      pb: 2,
                      borderTop: 1,
                      borderColor: "divider",
                      backgroundColor: "error.50",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ mb: 1, color: "error.main" }}
                    >
                      Are you sure you want to delete this membership?
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        justifyContent: "flex-end",
                      }}
                    >
                      <Button
                        size="small"
                        onClick={() => setDeleteConfirmId(null)}
                        sx={{ color: "text.secondary" }}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="small"
                        onClick={() => handleDelete(plan.id)}
                        sx={{ color: "error.main", fontWeight: "bold" }}
                        disabled={deleteMembershipMutation.isPending}
                      >
                        {deleteMembershipMutation.isPending ? (
                          <CircularProgress size={16} />
                        ) : (
                          "Delete"
                        )}
                      </Button>
                    </Box>
                  </Box>
                )}
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Form Dialog - Also here for when data exists */}
      <Dialog
        open={isFormOpen}
        onClose={handleCloseForm}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            {editingPlan ? "Edit Membership Plan" : "Add New Membership Plan"}
          </Typography>
          <IconButton onClick={handleCloseForm} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3, py: 1 }}>
            {/* Image Upload Section */}
            <Box>
              <Typography variant="subtitle2" gutterBottom fontWeight="medium">
                Membership Image
              </Typography>

              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImageUpload}
                accept="image/*"
                style={{ display: "none" }}
              />

              <Box
                onClick={() => fileInputRef.current?.click()}
                sx={{
                  border: "2px dashed",
                  borderColor: "grey.300",
                  borderRadius: 2,
                  p: 3,
                  textAlign: "center",
                  cursor: "pointer",
                  backgroundColor: "grey.50",
                  "&:hover": {
                    borderColor: "primary.main",
                    backgroundColor: "primary.50",
                  },
                }}
              >
                {imagePreview ? (
                  <Box>
                    <img
                      src={imagePreview}
                      alt="Preview"
                      style={{
                        width: "100%",
                        maxHeight: "200px",
                        objectFit: "cover",
                        borderRadius: "8px",
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ mt: 1, color: "text.secondary" }}
                    >
                      Click to change image
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    <UploadIcon
                      sx={{ fontSize: 48, color: "grey.400", mb: 1 }}
                    />
                    <Typography variant="body1" color="text.secondary">
                      Click to upload image
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      PNG, JPG, GIF up to 10MB
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {/* Name Field */}
            <TextField
              label="Membership Name"
              value={formData.name}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, name: e.target.value }))
              }
              error={!!formErrors.name}
              helperText={formErrors.name}
              fullWidth
              variant="outlined"
            />

            {/* Amount Field */}
            <TextField
              label="Amount"
              value={formData.amount}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, amount: e.target.value }))
              }
              error={!!formErrors.amount}
              helperText={formErrors.amount}
              fullWidth
              variant="outlined"
              type="number"
              InputProps={{
                startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
              }}
            />

            <FormControl fullWidth error={!!formErrors.type}>
              <InputLabel id="membership-type-label">
                Membership Type
              </InputLabel>
              <Select
                labelId="membership-type-label"
                label="Membership Type"
                value={formData.type}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, type: e.target.value }))
                }
              >
                {membershipTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.type && (
                <FormHelperText>{formErrors.type}</FormHelperText>
              )}
            </FormControl>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseForm} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isSubmitting}
            sx={{ borderRadius: 2, minWidth: "120px" }}
          >
            {isSubmitting ? (
              <CircularProgress size={20} color="inherit" />
            ) : editingPlan ? (
              "Update Plan"
            ) : (
              "Create Plan"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AddMemberships;